import { Input } from '@nike/frame-component-library';
import {
  isNone, isSome, match, Default,
} from '@nike/rcf-fp';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { provisionStore } from '../../../../utils/service-calls/fiskaly';
import { REQUIRED_FIELD } from '../../../../utils/validation/input-validation';
import { ButtonBlack, ButtonSubmit, StoreSelect } from '../../../reusable';

const SIRET_REGEX = /^[0-9]{14}$/;
const SIRET_MESSAGE = 'Siret must be exactly 14 digit number';
const ABSENT_ATTRIBUTE = 'ABSENT_ATTRIBUTE';

// Storeviews returns nested address fields — request the `address` object so
// the caller receives postalCode / city and any other subfields consistently.
const STOREVIEWS_FIELDS = ['address'];

const isBlank = (value) => isNone(value) || value === '';

const cleanAttribute = (value) => (value && value !== ABSENT_ATTRIBUTE ? value : '');

const validateRegex = (value, regex, msg) => (regex.test(value) ? '' : msg);

const validateRequired = (value, regex, regexMsg) => match(value)(
  [isBlank, REQUIRED_FIELD],
  [() => isSome(regex), () => validateRegex(value, regex, regexMsg)],
  [Default, ''],
);

const DEFAULT_FORM = {
  address: {
    city: '',
    postalCode: '',
    street: '',
    streetNumber: '',
  },
  siret: '',
  storeId: '',
  storeName: '',
};

const StoreForm = ({
  userToken, onProvisioned, onCancel, selectedCountry,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [selectedStore, setSelectedStore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const setField = (fieldName, value) => setFormData((prev) => ({ ...prev, [fieldName]: value }));

  const setAddressField = (fieldName, value) => setFormData((prev) => ({
    ...prev,
    address: { ...prev.address, [fieldName]: value },
  }));

  const handleStoreSelected = (store) => {
    setSelectedStore(store);
    setErrorMessage('');
    const storeAddress = store?.address || {};
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        city: cleanAttribute(storeAddress.city),
        postalCode: cleanAttribute(storeAddress.postalCode),
      },
      storeId: cleanAttribute(store?.id),
      storeName: cleanAttribute(store?.name),
    }));
  };

  const validateStoreId = ({ storeId }) => validateRequired(storeId);
  const validateStoreName = ({ storeName }) => validateRequired(storeName);
  const validateSiret = ({ siret }) => validateRequired(siret, SIRET_REGEX, SIRET_MESSAGE);
  const validateStreet = ({ address }) => validateRequired(address.street);
  const validateStreetNumber = ({ address }) => validateRequired(address.streetNumber);
  const validatePostalCode = ({ address }) => validateRequired(address.postalCode);
  const validateCity = ({ address }) => validateRequired(address.city);

  const anyFieldFailsValidation = (form) => [
    validateStoreId,
    validateStoreName,
    validateSiret,
    validateStreet,
    validateStreetNumber,
    validatePostalCode,
    validateCity,
  ].some((f) => f(form) !== '');

  const buildPayload = () => ({
    address: {
      city: formData.address.city,
      postalCode: formData.address.postalCode,
      street: formData.address.street,
      streetNumber: formData.address.streetNumber,
    },
    siret: formData.siret,
    storeId: formData.storeId,
    storeName: formData.storeName,
  });

  const submit = () => {
    setSaving(true);
    setErrorMessage('');
    const payload = buildPayload();
    console.log('Provisioning store with payload:', payload);
    return provisionStore(userToken, payload)
      .then(({ body }) => {
        onProvisioned(body);
        return body;
      })
      .catch((err) => {
        setErrorMessage(`Failed to provision store: ${err.message}`);
        setSaving(false);
      });
  };

  return (
    <main className="ncss-row ta-sm-c">
      <section className="ta-sm-l bg-primary-grey pt2-sm pb2-sm prl2-sm border">
        <header className="u-rounded bg-black va-sm-m pt2-sm pb2-sm mb2-sm">
          <span className="headline-3 pl3-sm text-color-primary-light">
            Select Store
          </span>
        </header>
        <article className="ncss-col-sm-12 va-sm-t mb2-sm">
          <StoreSelect
            fullWidth
            countryRestriction={selectedCountry}
            selectCountry={() => null}
            selectStore={handleStoreSelected}
            selectedStore={selectedStore}
            storeviewsFields={STOREVIEWS_FIELDS}
          />
          {selectedStore?.label && (
            <p className="body-4 mt2-sm" style={{ color: '#757575' }}>
              {`Selected: ${selectedStore.label}`}
            </p>
          )}
        </article>
      </section>
      {selectedStore && (
        <>
          <section className="ta-sm-l bg-primary-grey pt2-sm pb2-sm prl2-sm border mt4-sm">
            <header className="u-rounded bg-black va-sm-m pt2-sm pb2-sm mb2-sm">
              <span className="headline-3 pl3-sm text-color-primary-light">
                General
              </span>
            </header>
            <article className="ncss-col-sm-6 va-sm-t">
              <Input
                isDisabled
                errorMessage={validateStoreId(formData)}
                label="Store ID"
                value={formData.storeId}
                onChange={() => null}
              />
            </article>
            <article className="ncss-col-sm-6 va-sm-t">
              <Input
                errorMessage={validateStoreName(formData)}
                label="Store Name"
                value={formData.storeName}
                onChange={(e) => setField('storeName', e.target.value)}
              />
            </article>
            <article className="ncss-col-sm-6 va-sm-t">
              <Input
                errorMessage={validateSiret(formData)}
                label="Siret"
                value={formData.siret}
                onChange={(e) => setField('siret', e.target.value)}
              />
            </article>
          </section>
          <section className="ta-sm-l bg-primary-grey pt2-sm pb2-sm prl2-sm border mt4-sm">
            <header className="u-rounded bg-black va-sm-m pt2-sm pb2-sm mb2-sm">
              <span className="headline-3 pl3-sm text-color-primary-light">
                Address
              </span>
            </header>
            <article className="ncss-col-sm-6 va-sm-t">
              <Input
                errorMessage={validateStreet(formData)}
                label="Street"
                value={formData.address.street}
                onChange={(e) => setAddressField('street', e.target.value)}
              />
            </article>
            <article className="ncss-col-sm-6 va-sm-t">
              <Input
                errorMessage={validateStreetNumber(formData)}
                label="Street Number"
                value={formData.address.streetNumber}
                onChange={(e) => setAddressField('streetNumber', e.target.value)}
              />
            </article>
            <article className="ncss-col-sm-6 va-sm-t">
              <Input
                errorMessage={validatePostalCode(formData)}
                label="Postal Code"
                value={formData.address.postalCode}
                onChange={(e) => setAddressField('postalCode', e.target.value)}
              />
            </article>
            <article className="ncss-col-sm-6 va-sm-t">
              <Input
                errorMessage={validateCity(formData)}
                label="City"
                value={formData.address.city}
                onChange={(e) => setAddressField('city', e.target.value)}
              />
            </article>
          </section>
        </>
      )}
      <footer className="ncss-row">
        <ButtonSubmit
          isDisabled={anyFieldFailsValidation(formData)}
          isLoading={saving}
          label="Provision Store"
          onClick={submit}
        />
        {onCancel && (
          <ButtonBlack
            label="Cancel"
            onClick={onCancel}
          />
        )}
      </footer>
      {errorMessage && (
        <p className="text-color-error ta-sm-c body-2 mt2-sm">
          {errorMessage}
        </p>
      )}
    </main>
  );
};

StoreForm.defaultProps = {
  onCancel: null,
  onProvisioned: () => null,
  selectedCountry: null,
};

StoreForm.propTypes = {
  onCancel: PropTypes.func,
  onProvisioned: PropTypes.func,
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  userToken: PropTypes.string.isRequired,
};

export default StoreForm;
